import { Dispatch, ListenerEffectAPI } from '@reduxjs/toolkit';

import { setSentryUser, unsetSentryUser } from '/common/Sentry';
import { isTokenExpiringSoon, isValidToken } from '/helpers';
import { services2 } from '/api/services2';
import { systemRehydrate } from '/store/systemActions';
import { startAppListening } from '/store/listener';
import { authToolkit, IAuthState } from '/store/toolkits/auth';
import { snackbarService } from '/common/snackbarService';

const TOKEN_CHECK_INTERVAL_MS = 5 * 60 * 1000;

let tokenCheckInterval: number | undefined;

export function stopTokenRefresher() {
  if (tokenCheckInterval !== undefined) {
    clearInterval(tokenCheckInterval);
  }
}

async function refreshToken(serializedAuth: IAuthState) {
  const onError = () => {
    stopTokenRefresher();
    return authToolkit.initialState;
  };
  const data = await services2.authService.refreshToken(serializedAuth.tokens.refresh);
  return data.bifold(
    () =>
      services2.authService.invalidateToken().then((tokensData) =>
        tokensData.bifold(
          (tokens) => ({
            ...serializedAuth,
            tokens: {
              access: tokens.access,
              refresh: tokens.refresh,
            },
          }),
          onError
        )
      ),
    onError
  );
}

function parseAuth$(auth: string, fallback: IAuthState): Promise<IAuthState> {
  try {
    const serializedAuth = JSON.parse(auth) as IAuthState | undefined;

    if (!serializedAuth) {
      return Promise.resolve(fallback);
    }

    if (isValidToken(serializedAuth.tokens.access)) {
      return Promise.resolve(serializedAuth);
    }

    if (serializedAuth.tokens.refresh) {
      return refreshToken(serializedAuth);
    }

    const { googleRedirectURL, rememberMe }: IAuthState = serializedAuth;

    return Promise.resolve({
      ...fallback,
      ...(googleRedirectURL ? { googleRedirectURL } : null),
      ...(!rememberMe ? { rememberMe } : null),
    });
  } catch (e) {
    console.error(`Can't read auth state from localStorage`, e);

    return Promise.resolve(fallback);
  }
}

const getPersistedAuth = () => {
  const persistedAuth: string | null = localStorage.getItem('auth');
  const persistedAuthSession: string | null = sessionStorage.getItem('auth');

  const defaultState = authToolkit.initialState;

  return { persistedAuth, persistedAuthSession, defaultState };
};

const fetchIniAuthState = (): Promise<IAuthState> => {
  const { persistedAuth, persistedAuthSession, defaultState } = getPersistedAuth();

  if (persistedAuthSession) {
    return parseAuth$(persistedAuthSession, defaultState);
  }

  if (persistedAuth) {
    return parseAuth$(persistedAuth, defaultState);
  }

  return Promise.resolve(defaultState);
};

function startTokenRefresher<S extends { auth: IAuthState }>(
  api: ListenerEffectAPI<S, Dispatch>,
  onSuccess: (authState: IAuthState) => void,
  onError: (e: Error, snackbarMessage?: string) => void
) {
  stopTokenRefresher();

  tokenCheckInterval = window.setInterval(async () => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    const { auth } = api.getState();
    if (!auth?.tokens.refresh) {
      stopTokenRefresher();
      return;
    }

    try {
      const isExpiringSoon = isTokenExpiringSoon(auth.tokens.access);
      if (isExpiringSoon) {
        const updatedState = await refreshToken(auth);
        onSuccess(updatedState);
      }
    } catch (err) {
      onError(err as Error, 'Token refresher error');
    }
  }, TOKEN_CHECK_INTERVAL_MS);
}

const handleSuccess = <S>(authState: IAuthState, api: ListenerEffectAPI<S, Dispatch>) => {
  localStorage.setItem('auth', JSON.stringify(authState));
  services2.setAuthorization(authState.tokens.access);
  api.dispatch(authToolkit.actions.rehydrate(authState));
  api.dispatch(authToolkit.actions.updateServicesAuthorizationStatus(true));
  setSentryUser(authState.user.id.toString(), authState.user.name, authState.user.email);
};

const handleError = <S>(e: Error, api: ListenerEffectAPI<S, Dispatch>, snackbarMessage?: string) => {
  stopTokenRefresher();
  unsetSentryUser();
  snackbarService.error(snackbarMessage ?? e.message);
  console.error('auth.rehydrate$', e);
  api.dispatch(authToolkit.actions.rehydrate(authToolkit.initialState));
};

export const initListeners = () => {
  startAppListening({
    type: systemRehydrate.type,
    effect: async (_action, api) => {
      const onSuccess = (authState: IAuthState) => handleSuccess(authState, api);
      const onError = (e: Error, snackbarMessage?: string) => handleError(e, api, snackbarMessage);

      const state = api.getState();
      const user = authToolkit.selectors.user(state);
      const isAdmin = authToolkit.selectors.isAdmin(state);
      if (!!user && !isAdmin) {
        const perms = await services2.permsService.getPermsByCompany();
        api.dispatch(authToolkit.actions.getPermsByCompany(perms.serialize()));
      }
      fetchIniAuthState()
        .then((authState) => {
          handleSuccess(authState, api);
          startTokenRefresher(api, onSuccess, onError);
        })
        .catch((e: Error) => {
          onError(e, `Login: ${e.message}`);
        });
    },
  });
};
